/**
 * Capture the full canvas as a base64 JPEG data URL.
 * Always captures the entire 2000×1500 canvas area regardless of the
 * current pan/zoom, so images never have black areas from out-of-bounds regions.
 */
export function captureCanvas(stageRef) {
  const stage = stageRef?.current || stageRef;
  if (!stage || typeof stage.toDataURL !== 'function') return null;

  // Capture the full logical canvas (2000×1500), not the current viewport.
  // This avoids black regions caused by pan/zoom offsets.
  const CANVAS_W = 2000;
  const CANVAS_H = 1500;

  return stage.toDataURL({
    mimeType: 'image/jpeg',
    quality: 0.7,
    x: 0,
    y: 0,
    width: CANVAS_W,
    height: CANVAS_H,
    pixelRatio: 0.4,
  });
}

/**
 * Capture a specific vertical zone of the canvas for Exquisite Corpse.
 * Crops to the zone region so each snapshot shows just that zone.
 *
 * @param {Object} stageRef - Konva stage ref
 * @param {number} zoneIndex - 0-based zone index
 * @param {number} zoneCount - total zones (3, 4, or 5)
 * @returns {string|null} base64 JPEG data URL
 */
export function captureZone(stageRef, zoneIndex, zoneCount) {
  const stage = stageRef?.current || stageRef;
  if (!stage || typeof stage.toDataURL !== 'function') return null;

  const CANVAS_W = 2000;
  const CANVAS_H = 1500;
  const zoneH = CANVAS_H / (zoneCount || 3);

  return stage.toDataURL({
    mimeType: 'image/jpeg',
    quality: 0.7,
    x: 0,
    y: zoneIndex * zoneH,
    width: CANVAS_W,
    height: zoneH,
    pixelRatio: 0.5,
  });
}

/**
 * High-resolution capture for gallery exports and sharing.
 */
export function captureCanvasHD(stageRef) {
  const stage = stageRef?.current || stageRef;
  if (!stage || typeof stage.toDataURL !== 'function') return null;

  return stage.toDataURL({
    mimeType: 'image/png',
    x: 0,
    y: 0,
    width: 2000,
    height: 1500,
    pixelRatio: 2,
  });
}
